import { useEffect, useRef } from 'react';
import { Edit3, FolderPlus, Star, StarOff, Trash2 } from 'lucide-react';

interface FolderContextMenuProps {
  anchor: { x: number; y: number };
  isFavorite: boolean;
  /** Czy folder jest systemowy (Inbox/Sent/Drafts/Trash/Archive/Junk) — blokuje rename/delete. */
  isSystemFolder?: boolean;
  onFavoriteToggle: () => void;
  onRename: () => void;
  onCreateSubfolder: () => void;
  onDelete: () => void;
  onClose: () => void;
}

/**
 * Menu kontekstowe dla węzła drzewa IMAP. Otwierane prawym klikiem.
 * Zamyka się po: Escape, kliknięciu poza menu, wyborze akcji.
 */
export function FolderContextMenu({
  anchor,
  isFavorite,
  isSystemFolder = false,
  onFavoriteToggle,
  onRename,
  onCreateSubfolder,
  onDelete,
  onClose,
}: FolderContextMenuProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    window.addEventListener('keydown', onKey);
    // Delay attach żeby kliknięcie otwierające menu go nie zamknęło.
    const t = setTimeout(() => window.addEventListener('mousedown', onClick), 0);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onClick);
      clearTimeout(t);
    };
  }, [onClose]);

  return (
    <div
      ref={ref}
      className="fixed z-[60] bg-surface-raised border border-border rounded-lg shadow-2xl py-1 min-w-[200px]"
      style={{ left: anchor.x, top: anchor.y }}
    >
      <button
        onClick={() => { onFavoriteToggle(); onClose(); }}
        className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-text-secondary hover:bg-surface-hover hover:text-text-primary transition-colors"
      >
        {isFavorite ? <StarOff className="w-3.5 h-3.5" /> : <Star className="w-3.5 h-3.5" />}
        {isFavorite ? 'Usuń z ulubionych' : 'Dodaj do ulubionych'}
      </button>
      {!isSystemFolder && (
        <button
          onClick={() => { onCreateSubfolder(); onClose(); }}
          className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-text-secondary hover:bg-surface-hover hover:text-text-primary transition-colors"
        >
          <FolderPlus className="w-3.5 h-3.5" />
          Utwórz podfolder
        </button>
      )}
      {!isSystemFolder && (
        <>
          <button
            onClick={() => { onRename(); onClose(); }}
            className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-text-secondary hover:bg-surface-hover hover:text-text-primary transition-colors"
          >
            <Edit3 className="w-3.5 h-3.5" />
            Zmień nazwę
          </button>
          <div className="my-1 h-px bg-surface-hover/50" />
          <button
            onClick={() => { onDelete(); onClose(); }}
            className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-danger hover:bg-danger/10 transition-colors"
          >
            <Trash2 className="w-3.5 h-3.5" />
            Usuń folder
          </button>
        </>
      )}
      {isSystemFolder && (
        <div className="px-3 py-1.5 text-[10px] text-text-disabled italic">
          Folder systemowy — nie można zmienić nazwy ani usunąć
        </div>
      )}
    </div>
  );
}
